/**
 * Dialog for assigning a DID to a customer account.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Stack,
  Typography,
} from "@mui/material";
import { didsApi } from "../../api/dids";
import type { DID } from "../../types/did";

interface AssignDIDDialogProps {
  open: boolean;
  did: DID | null;
  onClose: () => void;
  onAssigned: (message: string) => void;
}

/** Assign-to-customer dialog. */
export default function AssignDIDDialog({
  open,
  did,
  onClose,
  onAssigned,
}: AssignDIDDialogProps) {
  const [customerId, setCustomerId] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCustomerId(did?.assigned_customer_id ?? "");
      setError("");
    }
  }, [open, did]);

  const handleClose = () => {
    setCustomerId("");
    setError("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!did) {
      return;
    }
    setError("");
    const trimmed = customerId.trim();
    if (!trimmed) {
      setError("Customer account ID is required.");
      return;
    }
    setSubmitting(true);
    try {
      if (did.status === "assigned") {
        await didsApi.unassign(did.id);
      }
      await didsApi.assign(did.id, { customer_id: trimmed });
      onAssigned(`DID ${did.phone_number} assigned to ${trimmed}.`);
      handleClose();
    } catch (err: unknown) {
      const detail =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err as any)?.response?.data?.detail || "Failed to assign DID.";
      setError(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign DID</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {did && (
            <Typography variant="body2" color="text.secondary">
              {did.phone_number} (area code {did.area_code}, $
              {did.monthly_cost.toFixed(2)}/month)
            </Typography>
          )}
          <TextField
            label="Customer account ID"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            placeholder="e.g. acct-123"
            required
            fullWidth
          />
          {did?.status === "assigned" && (
            <Alert severity="info">
              Currently assigned to {did.assigned_customer_id}. Re-assigning
              will release the old assignment first.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? "Assigning…" : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
